/**
 * Packing Suggestions Service
 * 
 * Generates packing list suggestions based on weather forecasts,
 * destination climate, and cultural norms
 */

import { fetchWeatherForecast, type WeatherForecast } from './weather';
import type { Trip } from './trips';

export type PackingSuggestion = {
  category: 'clothing' | 'essentials' | 'accessories' | 'electronics' | 'documents' | 'health';
  item: string;
  reason?: string;
  priority: 'high' | 'medium' | 'low';
  weatherBased?: boolean;
};

export type PackingSuggestions = {
  suggestions: PackingSuggestion[];
  weatherSummary?: {
    avgTemp: number;
    minTemp: number;
    maxTemp: number;
    hasPrecipitation: boolean;
    conditions: string[];
  };
};

/**
 * Generate packing suggestions based on weather forecast
 */
export async function generatePackingSuggestions(
  trip: Trip,
  weatherForecast?: WeatherForecast
): Promise<PackingSuggestions> {
  const suggestions: PackingSuggestion[] = [];
  let weatherSummary: PackingSuggestions['weatherSummary'];

  const forecastEntries = Array.isArray(weatherForecast?.forecast)
    ? weatherForecast.forecast.filter(
        (f) =>
          Number.isFinite(f?.temperature?.min) &&
          Number.isFinite(f?.temperature?.max)
      )
    : [];

  if (forecastEntries.length > 0) {
    const temps = forecastEntries.map(f => ({ 
      min: f.temperature.min, 
      max: f.temperature.max 
    }));
    const avgMin = temps.reduce((sum, t) => sum + t.min, 0) / temps.length;
    const avgMax = temps.reduce((sum, t) => sum + t.max, 0) / temps.length;
    const avgTemp = (avgMin + avgMax) / 2;
    const minTemp = Math.min(...temps.map(t => t.min));
    const maxTemp = Math.max(...temps.map(t => t.max));
    const hasPrecipitation = forecastEntries.some(f => 
      f.precipitation && (f.precipitation.probability > 0 || (f.precipitation.amount || 0) > 0)
    );
    const conditions = forecastEntries
      .map(f => f.condition)
      .filter((c): c is string => !!c)
      .filter((v, i, a) => a.indexOf(v) === i); // Unique conditions

    weatherSummary = {
      avgTemp: Math.round(avgTemp),
      minTemp: Math.round(minTemp),
      maxTemp: Math.round(maxTemp),
      hasPrecipitation,
      conditions,
    };

    // Temperature-based clothing suggestions
    if (avgMax > 25) {
      suggestions.push({
        category: 'clothing',
        item: 'Light clothing (shorts, t-shirts, summer dresses)',
        reason: `Average high: ${Math.round(avgMax)}°C`,
        priority: 'high',
        weatherBased: true,
      });
      suggestions.push({
        category: 'clothing',
        item: 'Sun hat and sunglasses',
        reason: 'Hot and sunny weather expected',
        priority: 'high',
        weatherBased: true,
      });
      suggestions.push({
        category: 'clothing',
        item: 'Sunscreen',
        reason: 'High UV exposure expected',
        priority: 'high',
        weatherBased: true,
      });
    } else if (avgMax > 15) {
      suggestions.push({
        category: 'clothing',
        item: 'Moderate clothing (light layers, long sleeves)',
        reason: `Average temp: ${Math.round(avgMin)}-${Math.round(avgMax)}°C`,
        priority: 'high',
        weatherBased: true,
      });
      suggestions.push({
        category: 'clothing',
        item: 'Light jacket or sweater',
        reason: 'Cooler evenings expected',
        priority: 'medium',
        weatherBased: true,
      });
    } else {
      suggestions.push({
        category: 'clothing',
        item: 'Warm clothing (jackets, sweaters, layers)',
        reason: `Average temp: ${Math.round(avgMin)}-${Math.round(avgMax)}°C`,
        priority: 'high',
        weatherBased: true,
      });
      suggestions.push({
        category: 'clothing',
        item: 'Warm accessories (gloves, scarf, hat)',
        reason: 'Cold weather expected',
        priority: 'high',
        weatherBased: true,
      });
    }

    // Precipitation-based suggestions
    if (hasPrecipitation) {
      suggestions.push({
        category: 'clothing',
        item: 'Rain gear (umbrella, rain jacket)',
        reason: 'Rain expected during trip',
        priority: 'high',
        weatherBased: true,
      });
      suggestions.push({
        category: 'accessories',
        item: 'Waterproof bag or cover',
        reason: 'Protect electronics and documents from rain',
        priority: 'medium',
        weatherBased: true,
      });
    }
  } else {
    // Default suggestions if no weather data
    suggestions.push({
      category: 'clothing',
      item: 'Appropriate clothing for destination',
      reason: 'Pack clothing suitable for destination climate and culture',
      priority: 'high',
      weatherBased: false,
    });
  }

  // Essential items (always included)
  const essentials: PackingSuggestion[] = [
    { category: 'essentials', item: 'Toothbrush', priority: 'high' },
    { category: 'essentials', item: 'Toothpaste', priority: 'high' },
    { category: 'essentials', item: 'Medications', priority: 'high' },
    { category: 'documents', item: 'Passport & IDs', priority: 'high' },
    { category: 'clothing', item: 'PJs', priority: 'high' },
    { category: 'electronics', item: 'Chargers/adapters', priority: 'high' },
    { category: 'electronics', item: 'Phone charger', priority: 'high' },
    { category: 'electronics', item: 'Power bank', priority: 'medium' },
    { category: 'accessories', item: 'Travel adapter', priority: 'high' },
    { category: 'health', item: 'First aid kit', priority: 'medium' },
    { category: 'essentials', item: 'Travel-sized toiletries', priority: 'medium' },
  ];

  suggestions.push(...essentials);

  return {
    suggestions,
    weatherSummary,
  };
}

/**
 * Get weather forecast for trip destination
 * Uses historical data if trip is far in the future (>30 days),
 * switches to actual forecasts as trip approaches
 */
export async function getWeatherForPacking(
  trip: Trip
): Promise<{ forecast: WeatherForecast | null; isHistorical: boolean; error?: string }> {
  const destination = trip.destination || trip.destinations?.[0]?.city;
  if (!destination) {
    return { forecast: null, isHistorical: false, error: 'No destination specified' };
  }

  const tripStart = trip.startDate instanceof Date ? trip.startDate : new Date(trip.startDate);
  const now = new Date();
  const daysUntilTrip = Math.ceil((tripStart.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const useHistoricalData = daysUntilTrip > 30;

  try {
    // Get coordinates for destination with detailed error logging
    const { getDestinationCoordinates } = await import('@/services/weather');
    console.log('[PackingSuggestions] Getting coordinates for:', destination);
    
    const coordsStartTime = Date.now();
    const coords = await getDestinationCoordinates(destination);
    const coordsDuration = Date.now() - coordsStartTime;
    
    if (!coords) {
      const error = `Failed to get coordinates for "${destination}" after ${coordsDuration}ms`;
      console.error('[PackingSuggestions]', error);
      return { forecast: null, isHistorical: false, error };
    }
    
    console.log('[PackingSuggestions] Got coordinates:', {
      city: coords.city,
      country: coords.country,
      lat: coords.latitude,
      lon: coords.longitude,
      duration: `${coordsDuration}ms`,
    });

    // Calculate days until trip
    console.log('[PackingSuggestions] Trip timing:', {
      tripStart: tripStart.toISOString().split('T')[0],
      today: now.toISOString().split('T')[0],
      daysUntilTrip,
    });

    // If trip is more than 30 days away, use historical data
    // Otherwise, use actual forecast
    if (useHistoricalData) {
      console.log('[PackingSuggestions] Using historical weather data (trip >30 days away)');
      // Use historical weather data for early planning
      const { getHistoricalWeatherForTrip } = await import('./historicalWeather');
      const historicalStartTime = Date.now();
      const historical = await getHistoricalWeatherForTrip(trip, coords);
      const historicalDuration = Date.now() - historicalStartTime;
      
      if (historical) {
        console.log('[PackingSuggestions] Historical weather retrieved:', {
          month: historical.month,
          avgTemp: historical.avgTemp,
          source: historical.source,
          duration: `${historicalDuration}ms`,
        });
        
        // Convert historical data to WeatherForecast format
        const tripEnd = trip.endDate instanceof Date ? trip.endDate : new Date(trip.endDate);
        const daysDiffRaw = Math.ceil((tripEnd.getTime() - tripStart.getTime()) / (1000 * 60 * 60 * 24));
        const daysDiff = Number.isFinite(daysDiffRaw) ? Math.max(1, daysDiffRaw) : 1;
        const forecastDays = Math.max(1, Math.min(daysDiff, 5));
        
        // Create forecast entries based on historical averages
        const forecast = Array.from({ length: forecastDays }, (_, i) => {
          const date = new Date(tripStart);
          date.setDate(date.getDate() + i);
          return {
            date: date.toISOString().split('T')[0],
            temperature: {
              min: historical.minTemp,
              max: historical.maxTemp,
              day: historical.avgTemp + 2,
              night: historical.avgTemp - 2,
            },
            condition: historical.avgPrecipitation > 50 ? 'Rain' : 'Clear',
            description: historical.avgPrecipitation > 50 ? 'Possible rain' : 'Generally clear',
            icon: historical.avgPrecipitation > 50 ? '10d' : '01d',
            humidity: 60,
            windSpeed: 10,
            precipitation: {
              probability: historical.rainyDays > 10 ? 60 : 30,
              amount: historical.avgPrecipitation > 50 ? historical.avgPrecipitation / historical.rainyDays : undefined,
            },
          };
        });

        return {
          forecast: {
            location: {
              city: coords.city,
              country: coords.country,
              latitude: coords.latitude,
              longitude: coords.longitude,
            },
            current: {
              temperature: historical.avgTemp,
              feelsLike: historical.avgTemp,
              condition: 'Historical Average',
              description: `Average conditions for ${tripStart.toLocaleDateString('en-US', { month: 'long' })}`,
              icon: '01d',
              humidity: 60,
              windSpeed: 10,
              windDirection: 180,
              pressure: 1013,
              visibility: 10000,
            },
            forecast,
          },
          isHistorical: true,
        };
      } else {
        const error = `Failed to get historical weather data after ${historicalDuration}ms`;
        console.error('[PackingSuggestions]', error);
        return { forecast: null, isHistorical: true, error };
      }
    }

    // For trips within 30 days, use actual forecast
    console.log('[PackingSuggestions] Using actual weather forecast (trip ≤30 days away)');
    const dayBeforeStart = new Date(tripStart);
    dayBeforeStart.setDate(dayBeforeStart.getDate() - 1);
    dayBeforeStart.setHours(0, 0, 0, 0);

    const forecastStartTime = Date.now();
    // Fetch weather forecast
    const forecast = await fetchWeatherForecast({
      latitude: coords.latitude,
      longitude: coords.longitude,
      city: coords.city,
      countryCode: coords.country,
      days: 5,
      targetDate: dayBeforeStart.toISOString().split('T')[0],
    });
    const forecastDuration = Date.now() - forecastStartTime;

    if (forecast) {
      console.log('[PackingSuggestions] Weather forecast retrieved:', {
        forecastDays: forecast.forecast?.length || 0,
        duration: `${forecastDuration}ms`,
      });
      return { forecast, isHistorical: false };
    } else {
      const error = `Failed to get weather forecast after ${forecastDuration}ms`;
      console.error('[PackingSuggestions]', error);
      return { forecast: null, isHistorical: false, error };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('[PackingSuggestions] Error getting weather for packing:', {
      error: errorMessage,
      destination,
      stack: error instanceof Error ? error.stack : undefined,
    });
    
    // Provide a minimal safe fallback when historical data was expected
    if (useHistoricalData) {
      const fallbackDate = tripStart.toISOString().split('T')[0];
      return {
        forecast: {
          location: {
            city: destination,
            country: '',
            latitude: 0,
            longitude: 0,
          },
          current: {
            temperature: 20,
            feelsLike: 20,
            condition: 'Fallback',
            description: 'Fallback historical estimate',
            icon: '01d',
            humidity: 60,
            windSpeed: 5,
            windDirection: 180,
            pressure: 1013,
            visibility: 10000,
          },
          forecast: [
            {
              date: fallbackDate,
              temperature: {
                min: 18,
                max: 22,
                day: 20,
                night: 18,
              },
              condition: 'Clear',
              description: 'Fallback historical estimate',
              icon: '01d',
              humidity: 60,
              windSpeed: 5,
              precipitation: { probability: 0 },
            },
          ],
        },
        isHistorical: true,
        error: errorMessage,
      };
    }

    return { forecast: null, isHistorical: useHistoricalData, error: errorMessage };
  }
}

